import 'react-toastify/dist/ReactToastify.css';
import { toast, ToastContainer } from "react-toastify";
import { useGetUserPointsAllocationLogs, useGetAllUsers } from "../../queries/user";
import TanStackTable from '../../components/TanStackTable';
import { createColumnHelper } from '@tanstack/react-table';
import { useMemo, useState, useEffect } from 'react';
import UserFilters from './UserFilters';
const ViewIcon = require("../../utils/media/icons/view_icon.png");

const PointsAllocationLogs = () => {

    const { data: rawData, isPending, isError } = useGetUserPointsAllocationLogs(toast);
    const { data: users } = useGetAllUsers(toast);
    
    const [filteredData, setFilteredData] = useState([]);
    const [filters, setFilters] = useState([]);

    // Extract unique activities from the data
    const uniqueActivities = useMemo(() => {
        if (!Array.isArray(rawData)) return [];
        const activities = [...new Set(rawData.map(item => item.activity))];
        return activities.map(activity => ({
            label: activity.charAt(0).toUpperCase() + activity.slice(1),
            value: activity
        }));
    }, [rawData]);

    // Initialize filters
    useEffect(() => {
        const userOptions = Array.isArray(users) ? users.map(user => ({
            placeholder: user.username,
            value: user._id
        })) : [];

        const activityOptions = uniqueActivities.map(activity => ({
            placeholder: activity.label,
            value: activity.value
        }));

        setFilters([
            {
                label: 'User',
                name: 'user',
                type: 'select',
                data: userOptions,
                value: '',
                placeholder: 'Select User'
            },
            {
                label: 'From Date',
                name: 'fromDate',
                type: 'date',
                value: '',
                placeholder: 'Select From Date'
            },
            {
                label: 'To Date',
                name: 'toDate',
                type: 'date',
                value: '',
                placeholder: 'Select To Date'
            },
            {
                label: 'Activity',
                name: 'activity',
                type: 'select',
                data: activityOptions,
                value: '',
                placeholder: 'Select Activity'
            }
        ]);
    }, [users, uniqueActivities]);

    // Initialize filtered data
    useEffect(() => {
        if (Array.isArray(rawData)) {
            setFilteredData(rawData);
        } else {
            setFilteredData([]);
        }
    }, [rawData]);

    const filterLogs = () => {
        if (!Array.isArray(rawData)) return;

        let filtered = [...rawData];

        filters.forEach(filter => {
            if (filter.value) {
                switch (filter.name) {
                    case 'user':
                        filtered = filtered.filter(item => 
                            item.userId?._id === filter.value
                        );
                        break;
                    case 'fromDate':
                        filtered = filtered.filter(item => 
                            new Date(item.createdAt) >= new Date(filter.value)
                        );
                        break;
                    case 'toDate':
                        filtered = filtered.filter(item => 
                            new Date(item.createdAt) <= new Date(filter.value + 'T23:59:59')
                        );
                        break;
                    case 'activity':
                        filtered = filtered.filter(item => 
                            item.activity === filter.value
                        );
                        break;
                    default:
                        break;
                }
            }
        });

        setFilteredData(filtered);
    };

    const clearFilters = () => {
        const resetFilters = filters.map(filter => ({
            ...filter,
            value: ''
        }));
        setFilters(resetFilters);
        setFilteredData(Array.isArray(rawData) ? rawData : []);
    };
    
    const columnHelper = createColumnHelper();

    const columns = useMemo(() => [
        columnHelper.accessor((row, index) => index + 1, {
            id: 'serialNumber',
            header: 'Srl. No.',
            cell: (info) => info.getValue(),
            enableSorting: false,
            enableColumnFilter: false,
        }),
        columnHelper.accessor('activity', {
            header: 'Activity',
            cell: (info) => (
                <span className="capitalize">{info.getValue()}</span>
            ),
        }),
        columnHelper.accessor('initialPoints', {
            header: 'Initial Points',
            cell: (info) => info.getValue(),
        }),
        columnHelper.accessor('allocatedPoints', {
            header: 'Allocated Points',
            cell: (info) => info.getValue(),
        }),
        columnHelper.accessor('userId.username', {
            header: 'User',
            cell: (info) => info.getValue(),
        }),
        columnHelper.accessor('challengeId.name', {
            header: 'Challenge',
            cell: (info) => info.getValue() || '-',
        }),
        columnHelper.accessor('createdAt', {
            header: 'Created At',
            cell: (info) => new Date(info.getValue()).toLocaleString('en-IN', { 
                month: 'short', 
                day: 'numeric', 
                year: 'numeric' 
            }),
            align: 'center',
        }),
        // columnHelper.accessor('_id', {
        //     id: 'actions',
        //     header: 'Actions',
        //     cell: (info) => (
        //         <button
        //             className="rounded p-1.5 bg-[rgba(52,199,89,0.1)] hover:bg-[rgba(52,199,89,0.2)] mx-1 min-h-7"
        //             // onClick={() => navigate('/challenges/proposed-challenges-details', { state: { proposedChallengeId: info.getValue() } })}
        //         >
        //             <img src={ViewIcon} alt="view" />
        //         </button>
        //     ),
        //     enableSorting: false,
        //     enableColumnFilter: false,
        //     align: 'center',
        // }),
    ], [columnHelper]);
    
    if(isPending){
        return(
            <div className="flex justify-center items-center w-full h-96">
                <h1 className="text-xl text-gray-400">Loading...</h1>
            </div>
        );
    }
    
    return (
        <main className="flex flex-col text-base font-semibold text-gray-900 rounded-none">
            <UserFilters 
                filters={filters}
                setFilters={setFilters}
                filterUsers={filterLogs}
                clearFilters={clearFilters}
            />
            <section className="flex flex-col pt-5 w-full mt-5 bg-white rounded-md shadow-[0px_0px_25px_rgba(94,92,154,0.1)] max-md:max-w-full">
                <div className="flex flex-col pr-px pl-6 w-full max-md:pl-5 max-md:max-w-full">
                    <h1 className="self-start text-lg font-semibold mb-2">User Points Allocation Logs</h1>
                    <TanStackTable
                        data={filteredData || []}
                        columns={columns}
                        enableFiltering={true}
                        enableSorting={true}
                        enablePagination={true}
                        defaultPageSize={10}
                        pageSizeOptions={[10, 20, 30, 50]}
                    />
                </div>
            </section>
            <ToastContainer
                position="top-right"
            />
        </main>
    );
}

export default PointsAllocationLogs;